import s from './MineSquadBoost.module.sass';

import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import {
  Block,
  Button,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { LoadingStatus } from '$store/reels/interface';
import { CoinSquad, CurrencyEnum, MineSquadBoostModel } from '$store/models';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { Link, useRoute } from 'react-router5';
import { useDispatch } from 'react-redux';
import { useTranslator, useUserInfo } from '$hooks';
import { Loader, UnitName } from '$shared/components';
import { formatBigNumber, formatCurrency } from '$utils';
import { IcChevron16, IcHelp28 } from '$assets';
import { MINE_SQUAD } from '$shared/constants/pages';
import { mainActions } from '$store/main';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { prettifyNumber } from '$utils/number';

const Upgrade: FC<{
  item: MineSquadBoostModel;
  loadData: () => void;
  squadId: number;
}> = ({ item, loadData, squadId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleUpgrade = () => {
    dispatch(toastActions.setToastLoading());
    Api.post(`/mine/squad/${squadId}/boosts/apply`, {
      name: item.name,
    })
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        dispatch(mainActions.setPopup(null));
        loadData();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  return (
    <div className={s.upgradeWrap}>
      <Placeholder
        compact
        iconSize={64}
        icon={<div className={s.upgradeIcon}>{item.smile}</div>}
        title={item.title}
      >
        {item.description}
      </Placeholder>
      <div className={s.upgradeInfo}>
        <List>
          <ListItem indicator={`+${formatCurrency(item.bonus, CurrencyEnum.PLACE)}`}>
            {t('mine_squad_boost_upgrade_bonus')}
          </ListItem>
          <ListItem indicator={formatCurrency(item.price, CurrencyEnum.PLACE)}>
            {t('mine_squad_boost_upgrade_price')}
          </ListItem>
        </List>
        {item.curLevel < item.maxLevels ? (
          <Button style={{ marginTop: 16 }} onClick={handleUpgrade}>
            {t('mine_squad_boost_upgrade_btn', {
              level: item.curLevel + 1,
            })}
          </Button>
        ) : (
          <Button style={{ marginTop: 16 }} disabled>
            {t('mine_squad_boost_upgrade_max')}
          </Button>
        )}
      </div>
    </div>
  );
};

const LevelInfo: FC<{ level: number }> = ({ level }) => {
  const t = useTranslator();
  const levels = [
    {
      members: 0,
      bonus: 1,
    },
    {
      members: 50,
      bonus: 2,
    },
    {
      members: 150,
      bonus: 4,
    },
    {
      members: 500,
      bonus: 8,
    },
    {
      members: 1000,
      bonus: 16,
    },
    {
      members: 2500,
      bonus: 32,
    },
    {
      members: 5000,
      bonus: 64,
    },
    {
      members: 10000,
      bonus: 128,
    },
  ];

  return (
    <div className={s.levelInfo}>
      <div className={s.levelInfoDescription}>
        {t('mine_squad_boost_levels_description')}
      </div>
      <List>
        {levels.map((item, i) => (
          <ListItem
            indicator={`+${item.bonus} PLACE`}
            key={i}
            color={level === i + 1 ? 'green' : 'default'}
          >
            <span>{i + 1}:</span>{' '}
            {t('mine_squad_boost_levels_count', {
              members: prettifyNumber(item.members),
            })}
          </ListItem>
        ))}
      </List>
    </div>
  );
};

export const MineSquadBoost: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();
  const squadId = route.params.id;

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('loading');
  const [info, setInfo] = useState<{
    squad: CoinSquad;
    upgrades: MineSquadBoostModel[];
  } | null>(null);
  const owner = useUserInfo(info?.squad.userId ?? 0);

  const upgrades = useMemo(() => {
    if (!info) {
      return [];
    }

    const copy = [...info.upgrades];
    copy.sort((a, b) => (a.minSquadLevel > b.minSquadLevel ? 1 : -1));
    return copy;
  }, [info]);

  const loadData = () => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/mine/squad/${squadId}/boosts`)
      .then((resp) => {
        dispatch(mainActions.setUser(resp.squadOwner));
        setInfo(resp);
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  };

  useLayoutEffect(() => {
    loadData();
  }, []);

  const handleLevel = () => {
    dispatch(
      mainActions.setPopup(
        <BottomSheet title={t('mine_squad_boost_levels')}>
          <LevelInfo level={info!.squad.level} />
        </BottomSheet>,
      ),
    );
  };

  const handleUpgrade = (item: MineSquadBoostModel) => {
    dispatch(
      mainActions.setPopup(
        <BottomSheet>
          <Upgrade item={item} loadData={loadData} squadId={squadId} />
        </BottomSheet>,
      ),
    );
  };

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={loadData}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !info) {
      return <Loader isWrapped />;
    }

    return (
      <>
        <div className={s.squadInfoWrap}>
          <Link routeName={MINE_SQUAD} routeParams={{ id: squadId }}>
            <Block className={s.squadInfo}>
              <div
                className={s.squadInfoPhoto}
                style={{ backgroundImage: `url(${info.squad.url})` }}
              />
              <div className={s.squadInfoCont}>
                <div className={s.squadInfoName}>
                  <UnitName unit={owner} />
                </div>
                <div className={s.squadInfoCaption}>
                  {t('mine_squad_coins_earned', {
                    coins: formatBigNumber(info.squad.earnAmount),
                  })}
                </div>
              </div>
              <div className={s.squadInfoChevron}>
                <IcChevron16 />
              </div>
            </Block>
          </Link>
        </div>
        <div className={s.boost}>
          <List title={` `} description={t('mine_squad_boost_current_info')}>
            <ListItem
              indicator={`+${prettifyNumber(info.squad.boost)} PLACE`}
              onClick={handleLevel}
            >
              {t('mine_squad_boost_current')}
            </ListItem>
          </List>
        </div>
        <div className={s.level}>
          <List>
            <ListItem icon={<IcHelp28 />} onClick={handleLevel}>
              {t('mine_squad_boost_squad_level')} <b>{info.squad.level}</b>
            </ListItem>
          </List>
        </div>
        <InlineHeader>{t('mine_squad_boost_upgrades')}</InlineHeader>
        <div className={s.upgrades}>
          <List>
            {upgrades
              .filter((item) => item.minSquadLevel <= info.squad.level)
              .map((item) => (
                <ListItem
                  onClick={() => handleUpgrade(item)}
                  leftIcon={
                    item.smile ? (
                      <div className={s.upgradesSmile}>{item.smile}</div>
                    ) : null
                  }
                  disabled={item.minSquadLevel > info.squad.level}
                  key={item.name}
                  indicator={item.price > 0 ? prettifyNumber(item.price) : null}
                  chevron
                >
                  {item.title} <span>{item.curLevel}lvl</span>
                </ListItem>
              ))}
          </List>
        </div>
        <InlineHeader type="sub">{t('mine_squad_boost_upgrades_not_avail')}</InlineHeader>
        <div className={s.upgrades}>
          <List>
            {upgrades
              .filter((item) => item.minSquadLevel > info.squad.level)
              .map((item) => (
                <ListItem
                  leftIcon={
                    item.smile ? (
                      <div className={s.upgradesSmile}>{item.smile}</div>
                    ) : null
                  }
                  disabled={item.minSquadLevel > info.squad.level}
                  key={item.name}
                  indicator={t('mine_squad_boost_upgrades_not_avail_info', {
                    level: item.minSquadLevel,
                  })}
                  chevron
                >
                  {item.title}
                </ListItem>
              ))}
          </List>
        </div>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('mine_squad_boost_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
